"use client"

import { useEffect } from "react"
import Link from "next/link"
import { Navbar } from "@/components/navbar"
import { Footer } from "@/components/footer"
import { AlertTriangle, Home, RefreshCw } from "lucide-react"

export default function Error({
    error,
    reset,
}: {
    error: Error & { digest?: string }
    reset: () => void
}) {
    useEffect(() => {
        // Log l'erreur pour le débogage
        console.error("Error boundary caught:", error)
    }, [error])

    return (
        <main className="min-h-screen bg-white dark:bg-dark-background flex flex-col">
            <Navbar />

            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="max-w-2xl w-full text-center">
                    {/* Error Icon */}
                    <div className="mb-8 flex justify-center">
                        <div className="bg-error/10 dark:bg-error/20 rounded-full p-8 animate-pulse">
                            <AlertTriangle className="w-24 h-24 text-error" />
                        </div>
                    </div>

                    {/* Message */}
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground dark:text-dark-foreground mb-4">
                        Une erreur est survenue
                    </h2>
                    <p className="text-lg text-foreground-secondary dark:text-dark-foreground-secondary mb-8 max-w-md mx-auto">
                        Désolé, quelque chose s'est mal passé. Nous travaillons à résoudre ce problème.
                    </p>

                    {/* Error Details (en dev uniquement) */}
                    {process.env.NODE_ENV === "development" && (
                        <div className="mb-8 p-4 bg-error/5 dark:bg-error/10 border border-error/20 rounded-lg max-w-xl mx-auto">
                            <p className="text-sm text-left text-foreground-secondary dark:text-dark-foreground-secondary font-mono break-all">
                                {error.message}
                            </p>
                            {error.digest && (
                                <p className="text-xs text-left text-foreground-secondary dark:text-dark-foreground-secondary mt-2">
                                    Digest: {error.digest}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <button
                            onClick={reset}
                            className="flex items-center gap-2 bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-medium transition w-full sm:w-auto justify-center"
                        >
                            <RefreshCw size={20} />
                            Réessayer
                        </button>

                        <Link
                            href="/"
                            className="flex items-center gap-2 bg-background-secondary dark:bg-dark-background-secondary hover:bg-background dark:hover:bg-dark-background text-foreground dark:text-dark-foreground px-6 py-3 rounded-lg font-medium transition border border-border dark:border-dark-border w-full sm:w-auto justify-center"
                        >
                            <Home size={20} />
                            Retour à l'accueil
                        </Link>
                    </div>

                    {/* Help */}
                    <div className="mt-12 pt-8 border-t border-border dark:border-dark-border">
                        <p className="text-sm text-foreground-secondary dark:text-dark-foreground-secondary mb-4">
                            Si le problème persiste :
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center">
                            <Link
                                href="/contact"
                                className="text-sm text-primary hover:underline"
                            >
                                Contactez-nous
                            </Link>
                            <span className="text-foreground-secondary dark:text-dark-foreground-secondary">•</span>
                            <button
                                onClick={() => window.location.reload()}
                                className="text-sm text-primary hover:underline"
                            >
                                Rafraîchir la page
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </main>
    )
}
